import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { queryOptions, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Bell, BellOff, CheckCheck, ChevronLeft } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  listMyNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/lib/notifications.functions";
import { Button } from "@/components/ui/button";

const notificationsQuery = queryOptions({
  queryKey: ["my-notifications"],
  queryFn: () => listMyNotifications(),
});

export const Route = createFileRoute("/_authenticated/notifications")({
  head: () => ({
    meta: [
      { title: "Notifications — Shields" },
      { name: "description", content: "Your Shields updates and alerts." },
      { name: "robots", content: "noindex" },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(notificationsQuery),
  component: NotificationsPage,
});

/** Friendly titles/bodies for each notification template. */
function renderNotification(template: string, payload: Record<string, unknown>) {
  const ref = typeof payload["reference"] === "string" ? payload["reference"] : null;
  switch (template) {
    case "booking_confirmed":
      return {
        title: "Booking received",
        body: `Your request${ref ? ` ${ref}` : ""} is in — we're matching you with local partners now.`,
      };
    case "account_deletion_requested":
      return {
        title: "Account deletion requested",
        body: "We've received your request. Your account and data will be removed once any open items are settled.",
      };
    case "achievement_awarded":
      return {
        title: "Achievement unlocked",
        body: "You've earned a new badge — check your profile to claim it.",
      };
    case "offer_received":
      return { title: "New job offer", body: "A job near you is waiting for your response." };
    case "bid_received":
      return { title: "New bid on your job", body: "A partner has bid on your booking — review and accept." };
    case "booking_assigned":
      return { title: "Job assigned", body: "Your booking now has a partner. Arrange a time that suits you." };
    default:
      return {
        title: template.replaceAll("_", " "),
        body: "",
      };
  }
}

export function NotificationsPage({ workspace = "customer" }: { workspace?: "customer" | "partner" }) {
  const { data: notifications } = useSuspenseQuery(notificationsQuery);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const markRead = useServerFn(markNotificationRead);
  const markAll = useServerFn(markAllNotificationsRead);
  const [markingAll, setMarkingAll] = useState(false);
  const [markingId, setMarkingId] = useState<string | null>(null);

  const isPartner = workspace === "partner";
  const unread = notifications.filter((n) => !n.read_at);

  async function handleMarkAll() {
    if (markingAll) return;
    setMarkingAll(true);
    try {
      const result = await markAll({});
      if (!result.ok) {
        toast.error(result.error ?? "Could not mark notifications as read");
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ["my-notifications"] });
      toast.success("All caught up");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not mark notifications as read");
    } finally {
      setMarkingAll(false);
    }
  }

  async function handleMarkOne(id: string) {
    if (markingId === id) return false;
    setMarkingId(id);
    try {
      const result = await markRead({ data: { id } });
      if (!result.ok) {
        toast.error(result.error ?? "Could not update this notification");
        return false;
      }
      await queryClient.invalidateQueries({ queryKey: ["my-notifications"] });
      return true;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update this notification");
      return false;
    } finally {
      setMarkingId(null);
    }
  }

  async function openCustomerNotification(
    id: string,
    template: string,
    payload: Record<string, unknown>,
    isUnread: boolean,
  ) {
    if (isUnread) {
      const marked = await handleMarkOne(id);
      if (!marked) return;
    }
    const bookingId = typeof payload["booking_id"] === "string" ? payload["booking_id"] : null;
    if (bookingId && [
      "booking_confirmed",
      "bid_received",
      "booking_assigned",
      "job_started",
      "completion_submitted",
      "schedule_proposed",
      "schedule_accepted",
      "schedule_declined",
    ].includes(template)) {
      await navigate({ to: "/bookings/$bookingId", params: { bookingId } });
      return;
    }
    if (template === "account_deletion_requested" || template === "achievement_awarded") {
      await navigate({ to: "/account" });
    }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card">
        <div className="section-pad mx-auto flex h-16 max-w-3xl items-center justify-between">
          <Link
            to={isPartner ? "/partner" : "/customer"}
            className="flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden /> {isPartner ? "Partner app" : "Customer app"}
          </Link>
          <span className="font-display font-bold">Notifications</span>
          {unread.length > 0 ? (
            <Button variant="ghost" size="sm" onClick={handleMarkAll} disabled={markingAll}>
              <CheckCheck className="mr-1.5 h-4 w-4" aria-hidden />
              {markingAll ? "Updating…" : "Mark all read"}
            </Button>
          ) : (
            <span className="w-24" />
          )}
        </div>
      </header>

      <main className={"product-page layout-activity section-pad mx-auto py-8 " + (workspace === "partner" ? "layout-partner-activity" : "layout-customer-activity")} >
        {notifications.length === 0 ? (
          <div className="card-surface flex flex-col items-center gap-3 p-10 text-center">
            <BellOff className="h-8 w-8 text-muted-foreground" aria-hidden />
            <p className="font-semibold">Nothing here yet</p>
            <p className="text-sm text-muted-foreground">
              {isPartner
                ? "New offers, assigned work and Partner achievements will show up here."
                : "Booking updates, Partner responses and Customer achievements will show up here."}
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {notifications.map((n) => {
              const { title, body } = renderNotification(
                n.template,
                (n.payload ?? {}) as Record<string, unknown>,
              );
              const isUnread = !n.read_at;
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() =>
                      isPartner
                        ? isUnread && handleMarkOne(n.id)
                        : openCustomerNotification(
                            n.id,
                            n.template,
                            (n.payload ?? {}) as Record<string, unknown>,
                            isUnread,
                          )
                    }
                    disabled={markingId === n.id}
                    className={`card-surface flex w-full items-start gap-3 p-4 text-left transition-colors disabled:cursor-wait disabled:opacity-60 ${
                      isUnread ? "border-primary/40" : "opacity-75"
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                        isUnread ? "bg-accent text-primary" : "bg-muted text-muted-foreground"
                      }`}
                    >
                      <Bell className="h-4 w-4" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="font-semibold">{title}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {new Date(n.created_at).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                          })}
                        </span>
                      </span>
                      {body && (
                        <span className="mt-0.5 block text-sm text-muted-foreground">{body}</span>
                      )}
                    </span>
                    {isUnread && (
                      <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
